import { useGetUsersQuery, useGetContractorsListQuery } from "./usersApiSlice";
import { useApproveUserMutation, useRejectUserMutation } from "../contractors/contractorsApiSlice";
import { Link } from "react-router-dom";
import { PAGE_SIZE } from "../../constants";
import { NAV } from "../../navLabels";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Icon, Loader } from "semantic-ui-react";
import React, { useState, useEffect } from "react";
import { ui } from "../../components/adminUi";
import { shortDate } from "../chat_sessions/MobileChatSessionList";
import { ChipRow, DateRangeField, ListContainer, PhoneDateRange, RatioScore, RowAction, RowButton, chip, Tag, cardAction, cardActions, cardBody, cardHeader, cardMeta, cardTitle, mutedCell, scoreColor, ListPagination, PageBand, SearchField, SortTh, SquareButton, firstCell, listCard, listPageStyle, pillButton, tableCard, td, th, cardStack } from "../../components/listPage";
import CustomDropdown from "../../components/CustomDropdown";
import CustomSnackbar from "../../components/CustomSnackbar";
import { baseUrl } from "../../app/api/apiSlice";

/**
 * A Firebase UID is not an identity. 14 of prod's 32 users sign in socially and carry a
 * 28-char UID as their username, which occupied a whole column and identified nobody.
 * Prefer the email; fall back to the username only when a human could read it.
 */
const looksLikeUid = value =>
  !!value &&
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ||
    /^[A-Za-z0-9]{20,}$/.test(value));

export const accountLabel = user => {
  if (user?.email) return user.email;
  if (user?.username && !looksLikeUid(user.username)) return user.username;
  return '';
};

const PROVIDER_ICONS = {
  'google.com': { name: 'google', color: '#4285F4', title: 'Google' },
  'apple.com': { name: 'apple', color: '#1d1d1f', title: 'Apple' },
};

const ProviderIcon = ({ provider }) => {
  const it = PROVIDER_ICONS[provider] || { name: 'mail outline', color: '#86868b', title: '이메일' };
  return (
    <Icon name={it.name} title={it.title} style={{ color: it.color, marginRight: '7px', opacity: 0.85 }} />
  );
};

/** `practice_seconds_used` is null only when the queryset did not annotate it. */
const practiceExhausted = user =>
  user?.practice_seconds_used != null &&
  user?.practice_budget_minutes > 0 &&
  user.practice_seconds_used >= user.practice_budget_minutes * 60;

const practiceMinutes = user =>
  user?.practice_seconds_used == null ? null : Math.round(user.practice_seconds_used / 60);

/** "41 / 52" style ratio, or null when there is nothing to count. */
/** Remedial questions not yet tried, amber while any are open. */
const Remedial = ({ user }) =>
  user.remedial_total ? (
    <span style={{ fontSize: '13px', whiteSpace: 'nowrap', color: user.remedial_open ? ui.amberFg : ui.muted, fontWeight: user.remedial_open ? 600 : 400 }}>
      {user.remedial_open ? `${user.remedial_open}문제 남음` : '모두 풂'}
      <span style={{ fontWeight: 400, color: ui.muted }}> / {user.remedial_total}</span>
    </span>
  ) : (
    <span style={{ color: ui.muted }}>—</span>
  );

/** The photo, or the initial on a tinted circle when there is none or it fails to load
    (expired social-login photo URLs); the stock sample avatar read as a real person. */
const Avatar = ({ user }) => {
  const [failed, setFailed] = useState(false);
  if (user.avatar_url && !failed) {
    return <img src={user.avatar_url} alt="" onError={() => setFailed(true)} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, backgroundColor: ui.chip, color: ui.muted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600 }}>
      {(user.full_name || user.username || '?').trim().charAt(0)}
    </span>
  );
};

/** Roles beside the name, replacing the 권한 column that read "—" on almost every row. */
const RoleLabels = ({ user }) => (
  <>
    {user.is_staff && <Tag tone="red">Admin</Tag>}
    {user.is_contractor_admin && <Tag tone="blue">파트너 관리자</Tag>}
  </>
);

// 전체 / 승인됨 / 대기중 (the API's is_approved; the export follows the same filter).
const APPROVAL = [['전체', null], ['승인됨', 'true'], ['대기중', 'false']];
// Done or not within the period (default: this quarter, the graded quota's). 평가 = a
// completed graded evaluation; 퀴즈 = any quiz answer. Click again to clear.
const COMPLETION = [
  ['evaluated', [['평가 완료', 'done'], ['평가 미완료', 'pending']]],
  ['quizzed', [['퀴즈 응시', 'done'], ['퀴즈 미응시', 'pending']]],
];

// 응대 스킬 as 달성률 % (score ÷ its criteria set's maximum, latest evaluation per session),
// as 직원 홈 shows it: raw points didn't compare across Apple's and LGU's criteria sets.
const achievementText = pct => `${Math.round(pct)}%`;

const formatActivity = user => {
  const mins = practiceMinutes(user);
  return `연습 ${mins == null ? '—' : `${mins}분`} / 평가 ${user?.evaluation_session_count ?? 0}회`;
};

const UsersList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const page_size = queryParams.get("page_size") || PAGE_SIZE;
  const ordering = queryParams.get("ordering");
  const search = queryParams.get("search");
  const contractor = queryParams.get("contractor");
  const is_approved = queryParams.get("is_approved");
  const evaluated = queryParams.get("evaluated");
  const quizzed = queryParams.get("quizzed");
  const start_date = queryParams.get("start_date");
  const end_date = queryParams.get("end_date");

  const [searchValue, setSearchValue] = useState(search || '');
  // The contractor filter lives behind the phone's filter button, as 퀴즈 내역's do.
  const [filtersOpen, setFiltersOpen] = useState(!!(contractor || start_date || end_date));

  // get query params from frontend url -> remove null ones to pass to backend
  let qp = { page, page_size, ordering, search, contractor, is_approved, evaluated, quizzed, start_date, end_date };
  qp = Object.entries(qp)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

  const { data, isLoading, isSuccess, isError, error } = useGetUsersQuery({
    ...qp,
  });

  const { data: contractorsData } = useGetContractorsListQuery();
  const contractors = Array.isArray(contractorsData)
    ? contractorsData
    : contractorsData?.results || [];
  const contractorOptions = contractors.map(c => ({
    key: c.id,
    text: c.name,
    value: c.id,
  }));

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const currentUser = useSelector(state => state.auth.user);
  const isAdminUser = currentUser?.is_staff || currentUser?.is_superuser || currentUser?.is_contractor_admin;
  const [approveUser] = useApproveUserMutation();
  const [rejectUser] = useRejectUserMutation();


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // 직원 목록 Excel (#197). Same fetch-then-blob download as 대화 내역's export: the API is
  // bearer-authenticated, so a plain <a href> would 401. Filters as on screen, no paging.
  const token = useSelector(state => state.auth.token);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(qp);
      params.delete('page');
      params.delete('page_size');
      const response = await fetch(`${baseUrl}/api/v1/accounts/users/export/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const stamp = new Date().toLocaleDateString('sv-SE').replace(/-/g, '');
      const url = window.URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err) {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  if (isLoading) return <Loader active />;
  if (isError) return null;


  const contractorDropdown = (
    <CustomDropdown
      searchable
      placeholder="소속 선택"
      options={contractorOptions}
      value={contractor}
      onChange={value => handleFilterChange('contractor', value)}
      icon="building outline"
      style={isMobile ? { width: '100%' } : { minWidth: '180px', flex: '0 0 auto' }}
    />
  );
  // The period shown is the API's (this quarter unless the URL sets one); clearing returns to it.
  const period = data.completion?.period || {};
  const clearPeriod = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('start_date');
    searchParams.delete('end_date');
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };
  const periodProps = {
    start: start_date || period.start,
    end: end_date || period.end,
    onChange: handleFilterChange,
    onClear: clearPeriod,
  };
  const searchField = <SearchField isMobile={isMobile} value={searchValue} onChange={setSearchValue} onSubmit={handleSearch} placeholder="이름, 이메일 검색" />;

  return (
    <div style={listPageStyle(isMobile)}>
      <PageBand
        isMobile={isMobile}
        icon="users"
        title={NAV.MY_STAFF}
        subtitle="직원 현황, 가입 승인, 학습 기록"
        actions={!isMobile && isAdminUser && (
          <button type="button" onClick={handleExport} disabled={exporting} style={{ ...pillButton(false, true), opacity: exporting ? 0.6 : 1 }}>
            엑셀 다운로드
            <Icon name={exporting ? 'spinner' : 'download'} loading={exporting} className="centered-icon" />
          </button>
        )}
      />

      <ListContainer isMobile={isMobile}>
        {isMobile ? (
          <>
            <div style={{ display: 'flex', gap: '8px' }}>
              {searchField}
              <SquareButton icon="sliders horizontal" label="필터" on={filtersOpen} set={!!(contractor || start_date || end_date)} onClick={() => setFiltersOpen(o => !o)} />
              {isAdminUser && <SquareButton icon="download" label="엑셀 다운로드" onClick={handleExport} loading={exporting} disabled={exporting} />}
            </div>
            {filtersOpen && (
              <div style={{ marginTop: '8px', display: 'grid', gap: '8px' }}>
                {contractorDropdown}
                <div style={{ fontSize: '12px', color: '#86868b' }}>평가·퀴즈 집계 기간</div>
                <PhoneDateRange {...periodProps} />
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {contractorDropdown}
            <span title="평가 완료·퀴즈 응시를 집계하는 기간 (기본: 이번 분기)"><DateRangeField {...periodProps} /></span>
            {searchField}
            <button type="button" onClick={handleSearch} style={pillButton(true)}>검색</button>
          </div>
        )}
        <ChipRow
          count={data.count}
          unit="명"
          groups={[
            APPROVAL.map(([label, value]) => (
              <button key={label} type="button" onClick={() => handleFilterChange('is_approved', value)} style={chip((is_approved || null) === value)}>
                {label}
              </button>
            )),
            ...COMPLETION.map(([param, options]) => options.map(([label, value]) => {
              const current = queryParams.get(param);
              const n = data.completion?.[param]?.[value];
              return (
                <button key={label} type="button" onClick={() => handleFilterChange(param, current === value ? null : value)} style={chip(current === value)}>
                  {label}{n != null && <span style={{ marginLeft: '5px', opacity: 0.6 }}>{n}</span>}
                </button>
              );
            })),
          ]}
        />

        {isMobile ? (
          <div style={cardStack}>
            {data.results.map(user => (
              <div key={user.id} style={listCard}>
                <div style={{ ...cardHeader, gap: '10px' }}>
                  <Avatar user={user} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={cardHeader}>
                      <span style={{ ...cardTitle, flex: '0 1 auto' }}>{user.full_name || '이름 없음'}</span>
                      <RoleLabels user={user} />
                    </div>
                    <div style={{ ...cardMeta, marginTop: '2px' }}>{accountLabel(user) || '—'}</div>
                  </div>
                  <Tag tone={user.is_approved ? 'green' : 'amber'}>{user.is_approved ? '승인됨' : '대기중'}</Tag>
                </div>
                <div style={cardMeta}>
                  {[user.contractor?.name, `가입 ${shortDate(user.created_at)}`, formatActivity(user)].filter(Boolean).join(' · ')}
                </div>
                <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 12px' }}>
                  {[
                    ['응대 스킬', user.average_evaluation_score != null ? <span style={{ fontWeight: 600, color: scoreColor(user.average_evaluation_score) }}>{achievementText(user.average_evaluation_score)}</span> : <span style={{ color: ui.muted }}>—</span>],
                    ['설명 정확도', <RatioScore num={user.pk_correct} den={user.pk_checked} />],
                    ['퀴즈 정답률', <RatioScore num={user.quiz_correct} den={user.quiz_questions} />],
                    ['맞춤 문제', <Remedial user={user} />],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ color: ui.muted }}>{label}</span>
                      {value}
                    </div>
                  ))}
                </div>
                <div style={cardActions}>
                  <Link to={`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&trainee=${user.id}`} style={cardAction(false, true)}>{NAV.SESSIONS}</Link>
                  <Link to={`/quiz/attempts?trainee=${user.id}`} style={cardAction(false, true)}>{NAV.QUIZ_HISTORY}</Link>
                  <Link to={`/userdashboard?userId=${user.id}`} style={cardAction(false, true)}>대시보드</Link>
                  {isAdminUser && !user.is_approved && <button type="button" onClick={() => approveUser(user.id)} style={cardAction(true)}>승인</button>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={tableCard}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <SortTh label="이름" field="full_name" ordering={ordering} onSort={handleSort} style={firstCell(th)} />
                    <SortTh label="소속" field="contractor__name" ordering={ordering} onSort={handleSort} />
                    <SortTh label="가입일" field="created_at" ordering={ordering} onSort={handleSort} />
                    <th style={th}>승인</th>
                    <SortTh label="응대 스킬" field="average_evaluation_score" ordering={ordering} onSort={handleSort} />
                    <SortTh label="설명 정확도" field="pk_rate" ordering={ordering} onSort={handleSort} />
                    <SortTh label="퀴즈 정답률" field="quiz_rate" ordering={ordering} onSort={handleSort} />
                    <SortTh label="맞춤 문제" field="remedial_open" ordering={ordering} onSort={handleSort} />
                    <th style={th}>활동</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {data.results.map(user => (
                    <tr key={user.id}>
                      <td style={firstCell(td)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Avatar user={user} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{user.full_name || <span style={{ color: ui.muted, fontWeight: 400 }}>이름 없음</span>}</span>
                              <RoleLabels user={user} />
                            </div>
                            {/* 계정 folded under the name: provider glyph + email (a Firebase UID shows nothing). */}
                            <div style={{ fontSize: '12px', color: ui.muted, whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={accountLabel(user)}>
                              <ProviderIcon provider={user.sign_in_provider} />
                              {accountLabel(user) || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{user.contractor ? user.contractor.name : '—'}</td>
                      <td style={{ ...td, ...mutedCell }}>{shortDate(user.created_at)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Tag tone={user.is_approved ? 'green' : 'amber'}>{user.is_approved ? '승인됨' : '대기중'}</Tag>
                          {isAdminUser && !user.is_approved && <RowButton primary onClick={() => approveUser(user.id)}>승인</RowButton>}
                          {isAdminUser && user.is_approved && <RowButton onClick={() => rejectUser(user.id)}>취소</RowButton>}
                        </div>
                      </td>
                      <td style={td}>
                        {user.average_evaluation_score != null ? (
                          <span style={{ fontWeight: 600, color: scoreColor(user.average_evaluation_score) }}>{achievementText(user.average_evaluation_score)}</span>
                        ) : (
                          <span style={{ color: ui.muted }}>—</span>
                        )}
                      </td>
                      <td style={td}><RatioScore num={user.pk_correct} den={user.pk_checked} /></td>
                      <td style={td}><RatioScore num={user.quiz_correct} den={user.quiz_questions} /></td>
                      <td style={td}><Remedial user={user} /></td>
                      <td style={td}>
                        <span title={`사전 연습 ${user.practice_budget_minutes ?? 0}분 중 사용`} style={{ fontSize: '13px', whiteSpace: 'nowrap', color: practiceExhausted(user) ? ui.redFg : ui.text }}>
                          {formatActivity(user)}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <RowAction icon="comments outline" title={NAV.SESSIONS} onClick={() => navigate(`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&trainee=${user.id}`)} />
                          <RowAction icon="history" title={NAV.QUIZ_HISTORY} onClick={() => navigate(`/quiz/attempts?trainee=${user.id}`)} />
                          <RowAction icon="chart bar outline" title="대시보드" onClick={() => navigate(`/userdashboard?userId=${user.id}`)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ListPagination page={Number(page) || 1} count={data.count} pageSize={Number(page_size)} onChange={pg => handlePaginationChange(null, { activePage: pg })} />
        <CustomSnackbar
          visible={exportFailed}
          title="다운로드 실패"
          message="엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요."
          onClose={() => setExportFailed(false)}
          type="error"
        />
      </ListContainer>
    </div>
  );
};
export default UsersList;
